<template>
  <div
    v-if="content"
    class="content-page"
  >
    <page class="content">
      <div class="content__breadcrumbs">
        <router-link
          :to="{ name: 'Folders', hash: `#team-${content.team.slug}` }"
          class="content__breadcrumb--team"
        >
          {{ content.team.name }}
        </router-link>
        <font-awesome-icon
          class="content__breadcrumb--angle-right"
          icon="angle-right"
        />
        <router-link
          :to="{ name: 'FoldersDetail', params: { folderId: content.folder.id } }"
          class="content__breadcrumb--folder"
        >
          {{ content.folder.name }}
        </router-link>
      </div>

      <page-title>{{ content.title }}</page-title>

      <content-tags :tags="content.tags" />

      <div class="content__created-at--mobile hidden-md">
        <span class="content__created-at-wrap">
          <font-awesome-icon
            class="content__created-at-icon"
            icon="clock"
          />
          {{ publishedAtWithFallback | fullDate }}
        </span>
      </div>
      <section class="content__info content__narrow">
        <div class="content__info-content--left">
          <button-input
            v-if="false"
            class="content__share-button"
            @click="$refs.contentShareModal.show()"
          >
            <font-awesome-icon icon="share-alt" />
            <span
              v-t="'p.contentPage.label.share'"
              class="visible-md"
            />

          </button-input>

          <dropdown-input
            :options="dropdownOptions"
            position="left"
            @select="handleDropdownSelect"
          >
            <button-input>
              <font-awesome-icon icon="cog" />
              <font-awesome-icon icon="angle-down" />
            </button-input>
          </dropdown-input>

          <div
            v-if="content.priority === 'high'"
            class="content__important"
          />
          <div class="content__comments-count">
            <font-awesome-icon
              class="content__comments-count-icon"
              icon="comments"
            />
            {{ content.commentsCount }}
          </div>
        </div>
        <div class="content__info-content--right">
          <div class="content__created-at--desktop visible-md">
            {{ publishedAtWithFallback | fullDate }}
          </div>
          <avatar
            :name="contentUserName"
            :img="contentUserAvatarUrl"
            class="content__author"
          />
        </div>
      </section>
    </page>

    <content-main-image />

    <page class="content__article-page">
      <article class="content__article">
        <div
          class="content__body content__narrow"
          v-html="bodyHtml"
        />
      </article>

      <content-attachments />
      <content-more />
    </page>
    <hr>

    <content-comments />

    <content-share-modal ref="contentShareModal" />
    <content-copy-modal ref="contentCopyModal" />
    <content-modal />
  </div>
</template>

<script>
import get from 'just-safe-get'
import isBlank from 'is-blank'
import { mapActions, mapState } from 'vuex'
import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import fullDate from '@/filters/fullDate'
import fullName from '@/filters/fullName'
import textToWeb from '@/filters/textToWeb'
import FormMixin from '@/mixins/forms/Form'
import PageMixin from '@/mixins/pages/Page'
import Avatar from '@/components/ui/Avatar'

import ContentModal from '@/components/contents/ContentModal'
import ContentShareModal from '@/components/contents/ContentShareModal'
import ContentCopyModal from '@/components/contents/ContentCopyModal'
import ContentMainImage from '@/components/contents/content/ContentMainImage'
import ContentMore from '@/components/contents/content/ContentMore'
import ContentComments from '@/components/contents/content/ContentComments'
import ContentAttachments from '@/components/contents/content/ContentAttachments'
import ContentTags from '@/components/contents/content/ContentTags'

const IMAGE_MAX_HEIGHT = 400

export default {
  name: 'ContentPage',
  mixins: [FormMixin, PageMixin],
  filters: {
    fullDate,
    fullName
  },
  components: {
    Avatar,
    ContentModal,
    ContentShareModal,
    ContentCopyModal,
    FontAwesomeIcon,
    ContentMainImage,
    ContentMore,
    ContentComments,
    ContentAttachments,
    ContentTags
  },
  data () {
    return {
      galleryIndex: null
    }
  },
  computed: {
    ...mapState({
      content: state => state.content.content,
    }),
    contentId () {
      return this.$route.params.contentId
    },
    dropdownOptions () {
      return [
        { label: 'Bearbeiten', value: 'edit', icon: 'edit' },
        { label: 'Kopieren', value: 'copy', icon: 'copy' },
        // { label: 'Verschieben', value: 'move', icon: 'share' },
        // { label: '"Wichtig" markieren', value: 'important', icon: 'exclamation-triangle' },
        { label: 'Löschen', value: 'delete', icon: 'trash' }
      ]
    },
    contentUserName () {
      return fullName(this.content.user)
    },
    contentUserAvatarUrl () {
      return this.content.user.avatarUrls.thumb
    },
    publishedAtWithFallback () {
      const { content } = this
      if (!content) return null
      return content.publishedAt || content.createdAt
    },
    bodyHtml () {
      return this.content && textToWeb(this.content.body)
    }
  },
  methods: {
    ...mapActions([
      'fetchContent',
      'openContentModalContent',
      'openContentCopyModal',
      'openConfirmModal',
      'closeConfirmModal',
      'destroyContent',
      'resetContent'
    ]),
    handleDropdownSelect ({ value }) {
      switch (value) {
        case 'edit':
          this.openContentModalContent(this.contentId)
          break
        case 'delete':
          this.openConfirmModal({
            title: 'Artikel löschen',
            message: `Den Artikel ${this.content.title} wirklich löschen?`,
            callback: () => {
              this.destroyContent(this.content.id)
                .then(this.closeConfirmModal)
                .then(() => {
                  this.$router.push({ path: `/foldaz/${this.content.folder.id}` })
                })
            }
          })
          break
        case 'copy':
          this.openContentCopyModal(this.content.id)
          break
        default:
          window.alert(`action: ${value}`)
      }
    }
  },
  created () {
    this.fetchContent(this.contentId)
      .then(() => {
        this.$emit('loadingDone')
      })
  },
  beforeDestroy () {
    this.resetContent()
  }
}
</script>

<style lang="sass">
@import '~styles/imports'

.content__breadcrumbs
  display: flex
  font-size: 14px
  justify-content: center
  margin-top: 33px

  +respond-max($md)
    align-items: center
    flex-direction: column
    justify-content: flex-start
    margin-top: 0

.content__breadcrumb--team,
.content__breadcrumb--folder
  color: var(--mainColor)
  font-weight: 500
  margin: 0 15px
  position: relative

.content__breadcrumb--angle-right
  position: relative
  top: 5px

  path
    fill: #868C96

  +respond-max($md)
    margin: -3px 0 7px
    transform: rotate(90deg)

.content__article-page
  border: none !important

.content-page .page-title
  margin-top: 14px

.content__created-at--mobile
  color: $color-grey-2
  font-size: 14px
  margin-right: 14px

.content__created-at-wrap
  align-items: center
  display: flex
  justify-content: center
  position: relative

.content__created-at-icon
  color: $color-grey-2
  font-size: 12px
  margin-right: 8px

.content__info
  display: flex
  justify-content: space-between
  margin: 53px auto 0

  +respond-max($md)
    margin-bottom: 12px
    margin-top: 34px

  .button-input
    margin-right: 10px

.content__dropdown-wrap
  position: relative

.content__info-content--left
  align-items: center
  display: flex
  justify-content: flex-start

.content__important
  background-color: #f01414
  border-radius: 20px
  color: #fff
  height: 20px
  margin-left: 12px
  width: 20px

  &:before
    color: #fff
    content: '!'
    display: block
    font-size: 15px
    font-weight: 700
    position: relative
    text-align: center
    top: -3px

.content__comments-count
  color: $color-grey-2
  font-size: 14px
  margin-left: 23px

.content__comments-count-icon
  font-size: 12px
  margin-right: 5px

.content__info-content--right
  align-items: center
  display: flex
  justify-content: flex-end

.content__created-at--desktop
  color: $color-grey-2
  font-size: 14px
  font-weight: 500
  margin-right: 14px

.content__author-image
  height: 42px
  width: 42px

.content__article
  margin-top: -70px

.content__body
  margin: 50px auto 0

  p
    margin-bottom: 30px

  .embedly-embed
    max-height: 320px
    max-width: 100%

.content__narrow
  max-width: 570px
  margin-left: auto
  margin-right: auto

.content__article-page .section-title
  font-size: 21px
  margin-bottom: 27px

.content__more-entry
  border-bottom: 1px solid $color-border-1
  display: flex
  font-size: 15px
  padding: 23px 0

  &:first-child
    border-top: 1px solid $color-border-1

.content__more-attribute,
.content__more-value
  +ellipsis
  flex: 1 1 50%

.content__more-attribute
  color: $color-grey-2
  flex: 0.3 1 30%

.content__more-value
  flex: 0.7 1 70%
  font-weight: 500
  text-align: right

.content__more-entry--url .content__more-value
  color: var(--linkColor)

.page.content
  background-color: transparent

.page.content
  margin-bottom: -132px
  overflow: visible

  +respond-max($md)
    margin-bottom: -50px
</style>



// WEBPACK FOOTER //
// src/pages/contents/ContentPage.vue